import axios from "axios";

const baseUrl = process.env.REACT_APP_DIFLEX_API_URL + "/users";

// GET
export async function getAllUsers(apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/Users/get-all-users`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to fetch users");
  }
}

export async function getUserRolesOnClaim(apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/Role/get-user-roles`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to fetch users");
  }
}

export async function getUserRoles(apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/Role/get-user-roles`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to fetch user roles");
  }
}


export async function getIsUserAdmin(apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/Role/is-user-admin`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to fetch data confirming user is admin");
  }
}

export async function getAllRoles(apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/Role/dip/get-all-roles`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to fetch roles");
  }
}

export async function getUsersByTenant(tenantPublicId, apiAccessToken) {
  try {
    const response = await axios.get(
      `${baseUrl}/User/by-tenant/${tenantPublicId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiAccessToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to fetch users");
  }
}

export async function getUser(userPublicId, apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/User/${userPublicId}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to fetch user");
  }
}

/**
 * Fetches the server-side settings that steer the SCO onboarding form: the activation
 * lead time, its explanatory text, and which list fields accept a value of the
 * orderer's own.
 *
 * @param {string} apiAccessToken - Bearer token for the Insight API
 * @returns {Promise<object>} The SCO form settings
 * @throws {Error} If the API request fails
 */
export async function getScoFormSettings(apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/User/sco-form-settings`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    // The API returns a plain string body for this endpoint, not a JSON error object.
    const detail =
      typeof error.response?.data === "string"
        ? error.response.data
        : error.response?.data?.message;

    throw new Error(detail || "Failed to fetch SCO form settings");
  }
}

// POST
export async function createUser(userData, apiAccessToken) {
  try {
    const response = await axios.post(`${baseUrl}/create-user`, userData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to create user");
  }
}

export async function createUserHardwareFormSubmit(userData, apiAccessToken) {
  try {
    const response = await axios.post(`${baseUrl}/User/create-user-hardware-form-submit`, userData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to create user");
  }
}


// PUT
export async function updateUser(userData, apiAccessToken) {
  try {
    const response = await axios.put(`${baseUrl}/update-user`, userData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to update user");
  }
}

/**
 * Updates a user from the customer portal.
 *
 * @param {string} billingGroupPublicId - Billing group public id
 * @param {string[]} roles - Insight roles for the user
 * @param {string} userPublicId - Public id of the user to update
 * @param {string} apiAccessToken - Bearer token for the Insight API
 * @param {object} [details] - Editable user details (PBI #316): jobTitle,
 *   officeLocation, country, mobilePhone, phone, company, department, plus
 *   managerUserPublicId and accountTypePublicId (PBI #318 — a changed account
 *   type queues a licence swap server-side). Omitted keys leave the stored
 *   value untouched; only keys present in this object are sent.
 * @returns {Promise<object>} The updated user
 * @throws {Error} If the API request fails
 */
export async function updateDipUser(
  billingGroupPublicId,
  roles=[],
  userPublicId,
  apiAccessToken,
  details = {}
) {
  try {
    const requestData = {
      billingGroupPublicId,
      roles: Array.isArray(roles) ? roles : [roles],
      userPublicId,
      ...details,
    };

    const response = await axios.put(
      `${baseUrl}/User/dip/${userPublicId}`,
      requestData,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiAccessToken}`,
        },
      }
);
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to update user");
  }
}

export async function updateBillingGroupOnUser(
  billingGroupPublicId,
  userPublicId,
  apiAccessToken
) {
  try {
    const requestData = {
      billingGroupPublicId,
      userPublicId,
      updatedDateTimeUTC: new Date().toISOString(),
    };

    const response = await axios.put(
      `${baseUrl}/User/update-billing-group-on-user`,
      requestData,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiAccessToken}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("API Error Response:", error.response?.data);
    throw new Error(error.response?.data?.message || "Failed to update billing group on user"
    );
  }
}

// DELETE — the API route is DELETE /users/User/{publicId} (Admin only); the old
// /delete-user/{id} path never existed and answered 404.
export async function deleteUser(userId, apiAccessToken) {
  try {
    const response = await axios.delete(`${baseUrl}/User/${encodeURIComponent(userId)}`, {
      headers: {
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to delete user");
  }
}